/* Login flow for magic-link authentication. */
(function (root) {
  'use strict';

  const app = root.App = root.App || {};
  const doc = root.document || null;
  const form = doc ? doc.getElementById('login-form') : null;
  const emailInput = doc ? doc.getElementById('login-email') : null;
  const statusEl = doc ? doc.getElementById('login-status') : null;
  const linkContainer = doc ? doc.getElementById('login-link') : null;
  const linkAnchor = doc ? doc.getElementById('login-link-anchor') : null;
  const helpEl = doc ? doc.getElementById('login-help') : null;
  const localeTrigger = doc ? doc.getElementById('login-locale-trigger') : null;
  const localeMenu = doc ? doc.getElementById('login-locale-menu') : null;
  const localeButtons = doc ? Array.from(doc.querySelectorAll('[data-login-locale-option]')) : [];
  const legalLinks = doc ? Array.from(doc.querySelectorAll('[data-login-legal-link]')) : [];
  const searchParams = new URLSearchParams(root.location ? root.location.search : '');
  const inviteToken = searchParams.get('invite_token');
  const nextPath = searchParams.get('next');
  const SERVER_ERROR_KEYS = {
    'Invite not found': 'ui.login.errorInviteNotFound',
    'Invite expired': 'ui.login.errorInviteExpired',
    'Invite already accepted': 'ui.login.errorInviteAccepted',
    'Invite email does not match this login': 'ui.login.errorInviteEmailMismatch',
    'Workspace is required': 'ui.login.errorWorkspaceRequired',
    'User is not authorized for this workspace': 'ui.login.errorUnauthorizedWorkspace',
  };

  function normalizeLocale(locale) {
    if (typeof app.locale?.normalizeLocale === 'function') {
      return app.locale.normalizeLocale(locale);
    }
    return String(locale || '').trim().toLowerCase() === 'cs' ? 'cs' : 'en';
  }

  function t(key, fallback) {
    if (typeof app.i18n?.t === 'function') {
      return app.i18n.t(key, null, { fallback });
    }
    return fallback;
  }

  function setStateLocale(locale) {
    app.state = app.state || {};
    app.state.identity = {
      ...(app.state.identity || {}),
      locale: normalizeLocale(locale),
    };
  }

  function getBrowserLocale() {
    const languages = root.navigator && Array.isArray(root.navigator.languages)
      ? root.navigator.languages
      : [];
    const firstLanguage = languages.find((value) => String(value || '').trim());
    return firstLanguage || (root.navigator ? root.navigator.language : '');
  }

  function resolveInitialLocale() {
    const datasetLocale = doc?.documentElement?.dataset?.defaultLocale || doc?.body?.dataset?.defaultLocale || '';
    if (typeof app.locale?.resolveLocale === 'function') {
      return app.locale.resolveLocale({
        storage: root.localStorage,
        cookieSource: doc,
        datasetLocale,
        fallbackLocale: getBrowserLocale() || datasetLocale || 'en',
      });
    }
    return normalizeLocale(datasetLocale || getBrowserLocale() || 'en');
  }

  function updateLocaleControls(locale) {
    const currentLocale = normalizeLocale(locale);
    localeButtons.forEach((button) => {
      const buttonLocale = normalizeLocale(button.dataset.loginLocaleOption);
      button.setAttribute('aria-checked', buttonLocale === currentLocale ? 'true' : 'false');
    });
  }

  function updateLegalLinks(locale) {
    const currentLocale = normalizeLocale(locale);
    legalLinks.forEach((link) => {
      const href = currentLocale === 'cs' ? link.dataset.csHref : link.dataset.enHref;
      if (href) {
        link.href = href;
      }
    });
  }

  function setLocaleMenuOpen(open) {
    if (!localeTrigger || !localeMenu) {
      return;
    }
    localeTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    localeMenu.hidden = !open;
  }

  function isLocaleMenuOpen() {
    return localeTrigger?.getAttribute('aria-expanded') === 'true';
  }

  function refreshLocalizedStatus() {
    if (!statusEl || !statusEl.dataset.i18nStatusKey) {
      return;
    }
    const key = statusEl.dataset.i18nStatusKey;
    setStatus(t(key, statusEl.textContent || ''), statusEl.dataset.statusError === 'true', key);
  }

  function applyLocale(locale, options = {}) {
    let nextLocale = normalizeLocale(locale);
    if (options.persist && typeof app.i18n?.setLocale === 'function') {
      nextLocale = app.i18n.setLocale(nextLocale, { target: doc });
    } else {
      if (options.persist && typeof app.locale?.persistLocale === 'function') {
        nextLocale = app.locale.persistLocale(nextLocale, {
          storage: root.localStorage,
          doc,
        });
      }
      setStateLocale(nextLocale);
      if (doc?.documentElement) {
        doc.documentElement.setAttribute('lang', nextLocale);
      }
      if (typeof app.i18n?.apply === 'function') {
        app.i18n.apply(doc);
      }
    }
    updateLocaleControls(nextLocale);
    updateLegalLinks(nextLocale);
    refreshLocalizedStatus();
    return nextLocale;
  }

  function setStatus(text, isError, statusKey) {
    if (!statusEl) return;
    statusEl.textContent = text || '';
    statusEl.style.color = isError ? '#b91c1c' : '';
    if (statusKey) {
      statusEl.dataset.i18nStatusKey = statusKey;
      statusEl.dataset.statusError = isError ? 'true' : 'false';
    } else {
      delete statusEl.dataset.i18nStatusKey;
      delete statusEl.dataset.statusError;
    }
  }

  function setStatusKey(key, isError, fallback) {
    setStatus(t(key, fallback), isError, key);
  }

  function detailToMessage(detail) {
    if (typeof detail === 'string') {
      return detail.trim();
    }
    if (Array.isArray(detail)) {
      const first = detail.find((item) => item);
      return detailToMessage(first);
    }
    if (detail && typeof detail === 'object') {
      const msg = detail.msg || detail.message || detail.detail;
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }
    }
    return '';
  }

  function resolveErrorMessage(detail) {
    const rawDetail = detailToMessage(detail);
    const key = SERVER_ERROR_KEYS[rawDetail];
    if (key) {
      return { text: t(key, rawDetail), key };
    }
    return {
      text: rawDetail || t('ui.login.loginFailed', 'Login failed.'),
      key: rawDetail ? '' : 'ui.login.loginFailed',
    };
  }

  async function readLoginPayload(res) {
    const contentType = String(res?.headers?.get?.('content-type') || '').toLowerCase();
    if (contentType.includes('application/json')) {
      try {
        const payload = await res.json();
        return payload && typeof payload === 'object' ? payload : {};
      } catch (_) {
        return {};
      }
    }
    try {
      const text = typeof res?.text === 'function' ? await res.text() : '';
      return text ? { detail: text } : {};
    } catch (_) {
      return {};
    }
  }

  async function submitLogin(event) {
    event.preventDefault();
    if (!emailInput) return;
    const email = emailInput.value.trim().toLowerCase();
    if (!email) {
      setStatusKey('ui.login.enterEmail', true, 'Enter a valid email address.');
      return;
    }
    setStatusKey('ui.login.sending', false, 'Sending sign-in link…');
    if (linkContainer) linkContainer.hidden = true;
    try {
      const body = inviteToken ? { email, invite_token: inviteToken } : { email };
      if (nextPath) {
        body.next = nextPath;
      }
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await readLoginPayload(res);
      if (!res.ok) {
        const errorMessage = res.status >= 500
          ? resolveErrorMessage('')
          : resolveErrorMessage(payload && payload.detail);
        const error = new Error(errorMessage.text);
        error.i18nKey = errorMessage.key;
        throw error;
      }
      if (helpEl) helpEl.hidden = true;
      if (payload.magic_link && linkContainer && linkAnchor) {
        linkAnchor.href = payload.magic_link;
        linkContainer.hidden = false;
        setStatusKey('ui.login.devLinkReady', false, 'Dev link ready:');
      } else {
        setStatusKey('ui.login.checkEmail', false, 'Check your email for the sign-in link.');
      }
    } catch (err) {
      if (helpEl) helpEl.hidden = false;
      if (err && err.i18nKey) {
        setStatusKey(err.i18nKey, true, err.message || 'Login failed.');
        return;
      }
      setStatus(err && err.message ? err.message : t('ui.login.loginFailed', 'Login failed.'), true);
    }
  }

  applyLocale(resolveInitialLocale(), { persist: false });

  if (localeTrigger) {
    localeTrigger.addEventListener('click', () => {
      setLocaleMenuOpen(!isLocaleMenuOpen());
    });
  }

  localeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      applyLocale(button.dataset.loginLocaleOption, { persist: true });
      setLocaleMenuOpen(false);
      localeTrigger?.focus?.();
    });
  });

  if (doc) {
    doc.addEventListener('click', (event) => {
      if (!isLocaleMenuOpen()) {
        return;
      }
      const target = event.target;
      if (
        localeTrigger?.contains?.(target)
        || localeMenu?.contains?.(target)
      ) {
        return;
      }
      setLocaleMenuOpen(false);
    });
    doc.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isLocaleMenuOpen()) {
        return;
      }
      setLocaleMenuOpen(false);
      localeTrigger?.focus?.();
    });
  }

  if (form) {
    form.addEventListener('submit', submitLogin);
  }

  app.login = {
    applyLocale,
    resolveInitialLocale,
    setLocaleMenuOpen,
  };
})(typeof window !== 'undefined' ? window : globalThis);
